/*
  Cart add-ons strip (snippets/cart-upsells.liquid).

  The strip is rendered OUTSIDE <cart-items>, which is what makes this class
  necessary. Dawn's CartItems.onCartUpdate() assigns a freshly fetched section
  to cart-items.innerHTML on any cart update it did not publish itself, so
  anything inside that element — .js-contents or not — is destroyed on every
  add. Out here the theme never touches the strip, and in exchange the strip is
  responsible for staying truthful about the cart.

  It does that by toggling cards rather than creating or destroying them. The
  snippet renders a card for every candidate, including products already in the
  cart (those start hidden), and syncToCart() decides which are shown from the
  cartUpdate payload. So a product added here hides its card, and a product
  removed from the cart anywhere on the page gets its card back — which a strip
  that deleted its own nodes could never do.
*/
class CartUpsells extends HTMLElement {
  constructor() {
    super();
    this.pending = false;
    this.addEventListener('click', this.onClick.bind(this));

    /*
      :focus-visible ignores mouse clicks on a button, but the spec makes it
      always match on controls that expect keyboard input — which includes
      <select>. So clicking the size dropdown draws the focus ring even though
      no keyboard was involved. Marking pointer-initiated focus lets the
      stylesheet suppress the ring for that case only; tabbing still rings,
      because a keyboard focus never sets the attribute.

      pointerdown fires before focus, so the attribute is in place by the time
      :focus-visible is evaluated. It is cleared on the way out so the same
      control rings normally if it is tabbed to later.
    */
    this.addEventListener('pointerdown', (event) => {
      const control = event.target.closest('button, select, a');
      if (control) control.dataset.pointerFocus = 'true';
    });

    this.addEventListener(
      'focusout',
      (event) => {
        if (event.target && event.target.dataset) delete event.target.dataset.pointerFocus;
      },
      true
    );
  }

  connectedCallback() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    /*
      Every cart change on the page, whatever caused it — a quantity change, a
      line removal, or our own add. Dawn's cart.js publishes the full cart JSON
      as cartData, which carries items[].product_id, so one handler covers all
      of them and the strip never has to track what changed.
    */
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event && event.cartData) this.syncToCart(event.cartData);
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
  }

  onClick(event) {
    // The strip sits inside <form id="cart">; the buttons are type="button" so
    // they never submit it, but stop the click from doing anything else either.
    const toggle = event.target.closest('[data-upsell-toggle]');
    if (toggle) {
      event.preventDefault();
      this.togglePanel(toggle);
      return;
    }

    const button = event.target.closest('[data-upsell-add]');
    if (!button) return;

    event.preventDefault();
    this.addVariant(button);
  }

  /*
    Multi-variant cards rest as image and price only; the + on the image opens
    the picker. Only one is open at a time — several open pickers in a 150px
    track makes the row ragged, and the point of the disclosure is to keep the
    resting state clean.
  */
  togglePanel(toggle) {
    const panel = document.getElementById(toggle.getAttribute('aria-controls'));
    if (!panel) return;

    const opening = panel.hidden;

    this.querySelectorAll('[data-upsell-toggle]').forEach((other) => {
      const otherPanel = document.getElementById(other.getAttribute('aria-controls'));
      if (!otherPanel) return;
      otherPanel.hidden = true;
      other.setAttribute('aria-expanded', 'false');
    });

    panel.hidden = !opening;
    toggle.setAttribute('aria-expanded', String(opening));

    // Focus is deliberately left on the toggle. aria-expanded/aria-controls
    // already tell a screen reader the panel opened, and the select is the very
    // next thing in the tab order, so moving focus adds nothing and takes it
    // away from the control the user pressed.
  }

  variantIdFor(button) {
    if (button.dataset.variantId) return button.dataset.variantId;

    const item = button.closest('.cart-upsells__item');
    const select = item && item.querySelector('[data-upsell-variant-select]');
    return select ? select.value : null;
  }

  /*
    Mirrors cart.js's getSectionsToRender(), minus the sections this theme does
    not render: header-custom.liquid has no #cart-icon-bubble, and the live
    region only exists on the cart page. Anything missing is filtered out rather
    than throwing part-way through the replace loop.
  */
  sectionsToRender() {
    return [
      { id: 'main-cart-items', selector: '.js-contents' },
      { id: 'main-cart-footer', selector: '.js-contents' },
    ].filter((section) => document.getElementById(section.id));
  }

  setBusy(button, busy) {
    button.toggleAttribute('disabled', busy);
    button.classList.toggle('cart-upsells__add--busy', busy);
  }

  /*
    The strip's own live region first, the cart page's as a fallback.

    Not the other way round: #cart-live-region-text lives inside <cart-items>,
    and Dawn replaces that element's innerHTML moments after this runs, which
    destroys the node before a screen reader has read it. The strip sits outside
    <cart-items>, so its own region survives the update.
  */
  announce(message) {
    const region =
      this.querySelector('[data-upsell-status]') || document.getElementById('cart-live-region-text');
    if (region) region.textContent = message;
  }

  addVariant(button) {
    const variantId = this.variantIdFor(button);
    if (!variantId || this.pending) return;

    const productTitle = button.dataset.productTitle || '';

    this.pending = true;
    this.setBusy(button, true);

    const body = JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] });

    fetch(`${routes.cart_add_url}`, { ...fetchConfig('javascript'), ...{ body } })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          // Shopify reports add failures (sold out, quantity rules) as a status
          // code on an otherwise 200 response.
          this.announce(response.description || response.message || '');
          return null;
        }

        this.announce(productTitle ? `${productTitle} added` : 'Added to cart');

        // Optimistic: hide now, then let the publish at the end of refresh()
        // reconcile every card against the cart that came back.
        this.hideCard(button);

        return this.refresh(variantId);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.pending = false;
        // The strip is usually replaced before this runs, so guard against
        // touching a detached button.
        if (button.isConnected) this.setBusy(button, false);
      });
  }

  /*
    Hidden, never removed — a removed node could not come back when the shopper
    takes that product out of the cart again. Called the moment the add succeeds
    so the card goes immediately, rather than after syncToCart() has waited on
    two network round trips.
  */
  hideCard(button) {
    const card = button.closest('.cart-upsells__item');
    if (!card) return;

    this.closePanelIn(card);
    card.hidden = true;
    this.resetSlider();
  }

  /*
    Shows exactly the candidates that are not in the cart, capped at the
    section's limit, and hides the strip when that leaves nothing.

    Reconciling against the whole cart rather than reacting to what changed
    means the same code handles an add from here, a line removed from the cart
    table, and a quantity change that empties a line — and a missed or
    out-of-order event cannot leave the strip disagreeing with the cart.
  */
  syncToCart(cartData) {
    if (!cartData || !Array.isArray(cartData.items)) return;

    /*
      An emptied cart has to be handled before the per-card pass, not by it: an
      empty items array means nothing is in the cart, so every card would test
      as showable and the strip would sit above the empty-cart message. This is
      the JS equivalent of the snippet's `cart.item_count > 0` gate.
    */
    if (cartData.item_count === 0) {
      this.hidden = true;
      return;
    }

    // product_id is a number in the cart JSON and a string in the dataset.
    const inCart = new Set(cartData.items.map((line) => String(line.product_id)));
    const limit = Number(this.dataset.limit) || Infinity;
    let visible = 0;

    this.querySelectorAll('.cart-upsells__item').forEach((card) => {
      const show = !inCart.has(card.dataset.productId) && visible < limit;

      if (!show) this.closePanelIn(card);
      card.hidden = !show;
      if (show) visible += 1;
    });

    this.hidden = visible === 0;
    this.resetSlider();
  }

  /*
    A card's picker is left open if it is hidden mid-interaction, so it would be
    open again the next time the card appears. Reset it with the toggle's
    aria-expanded, which would otherwise disagree with the panel.
  */
  closePanelIn(card) {
    const toggle = card.querySelector('[data-upsell-toggle]');
    if (!toggle) return;

    const panel = document.getElementById(toggle.getAttribute('aria-controls'));
    if (panel) panel.hidden = true;
    toggle.setAttribute('aria-expanded', 'false');
  }

  /*
    SliderComponent caches its slide list at construction and only rebuilds it
    in initPages(), which runs on construction and from its ResizeObserver.
    Showing or hiding a card need not change the track's size, so that observer
    may never fire — leaving the arrows counting slides that are no longer
    there, or ignoring ones that now are. resetPages() re-queries first.
  */
  resetSlider() {
    const slider = this.querySelector('slider-component');
    if (slider && typeof slider.resetPages === 'function') slider.resetPages();
  }

  /*
    Re-renders the cart items and totals only — the strip is outside them now.
    Each section is fetched with its own GET rather than asking /cart/add.js to
    render them inline via its `sections` parameter, because `?section_id=` is
    the same request a refresh makes and so always agrees with what a refresh
    would show.

    The cart is fetched alongside the sections rather than after them —
    subscribers need the item count and there is no reason to serialise the two
    round trips.
  */
  refresh(variantId) {
    const sections = this.sectionsToRender();

    const rendered = Promise.all(
      sections.map((section) => {
        const sectionId = document.getElementById(section.id).dataset.id;
        return fetch(`${routes.cart_url}?section_id=${encodeURIComponent(sectionId)}`)
          .then((response) => response.text())
          .then((html) => ({ section, html }));
      })
    );

    const cart = fetch(`${routes.cart_url}.js`).then((response) => response.json());

    return Promise.all([rendered, cart]).then(([results, cartData]) => {
      results.forEach(({ section, html }) => this.replaceSection(section, html));
      this.publishCartUpdate(variantId, cartData);
    });
  }

  replaceSection(section, html) {
    const container = document.getElementById(section.id);
    if (!container) return;

    const parsed = new DOMParser().parseFromString(html, 'text/html');

    /*
      Both lookups are scoped to the section's own container, with no unscoped
      fallback. main-cart-footer renders a .js-contents per block, so a bare
      '.js-contents' query can match a different section's block and inject its
      markup into the cart items area.
    */
    const source = parsed.querySelector(`#${section.id} ${section.selector}`);
    const target = container.querySelector(section.selector);
    if (source && target) target.innerHTML = source.innerHTML;
  }

  /*
    custom.js keeps the header bag count in step from cartData.item_count, and
    its wholesale handler reloads the page on this event because a wholesale
    cart cannot be repriced in place. Both come for free by publishing here.
  */
  publishCartUpdate(variantId, cartData) {
    if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    publish(PUB_SUB_EVENTS.cartUpdate, {
      source: 'cart-upsells',
      productVariantId: variantId,
      cartData,
    });
  }
}

customElements.define('cart-upsells', CartUpsells);
